// src/pages/auth/SignUp.tsx - Added Google OAuth support
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User, Eye, EyeOff, Zap, Crown, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GoogleOAuthButton } from '@/components/auth/GoogleOAuthButton'; // ADDED: Google OAuth
import toast from 'react-hot-toast';

const signUpSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
      'Password must contain uppercase, lowercase, number and special character'),
  confirmPassword: z.string(),
  terms: z.boolean().refine(val => val, 'You must accept the terms and conditions'),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const otpSchema = z.object({
  code: z.string().length(6, 'Verification code must be 6 digits'),
});

type SignUpForm = z.infer<typeof signUpSchema>;
type OTPForm = z.infer<typeof otpSchema>;

type SignUpStep = 'signup' | 'verify' | 'success';

export const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, confirmSignUp, signIn, signInWithGoogle, isLoading, error, clearError } = useAuthStore(); // ADDED: signInWithGoogle
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState<SignUpStep>('signup');
  const [signupData, setSignupData] = useState<{ email: string; password: string; name: string } | null>(null);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // ADDED: Check if Google OAuth is enabled
  const isGoogleEnabled = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === 'true';

  // Sign up form
  const signupForm = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
  });

  // OTP verification form
  const otpForm = useForm<OTPForm>({
    resolver: zodResolver(otpSchema),
  });

  // Countdown timer for resend
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const onSignUpSubmit = async (data: SignUpForm) => {
    try {
      clearError();
      await signUp(data.email, data.password, data.name);
      
      // Store signup data for verification step
      setSignupData({
        email: data.email,
        password: data.password,
        name: data.name,
      });
      
      setStep('verify');
      setCountdown(60); // 60 second countdown for resend
      toast.success('Verification code sent to your email!');
      
    } catch (error: any) {
      toast.error(error.message || 'Sign up failed');
    }
  };

  const onOTPSubmit = async (data: OTPForm) => {
    if (!signupData) {
      toast.error('Signup data not found. Please try again.');
      setStep('signup');
      return;
    }

    try {
      clearError();
      
      // Confirm the signup with OTP
      await confirmSignUp(signupData.email, data.code);
      
      // Auto sign-in after successful verification
      // New users from email signup should always go to onboarding
      const redirectPath = await signIn(signupData.email, signupData.password);
      
      setStep('success');
      
      // Navigate to onboarding (new users always need onboarding)
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
        toast.success('Account verified successfully! Welcome to TubeOps! 🎉');
      }, 2000);
      
    } catch (error: any) {
      const errorMessage = error.message || 'Verification failed';
      
      if (errorMessage.includes('Invalid verification code')) {
        toast.error('Invalid verification code. Please check your email and try again.');
      } else if (errorMessage.includes('ExpiredCodeException')) {
        toast.error('Verification code has expired. Please request a new one.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  // ADDED: Google sign-up handler (same as sign-in for OAuth)
  const handleGoogleSignUp = async () => {
    try {
      clearError();
      await signInWithGoogle(); // This will redirect, so no code after this will execute
    } catch (error: any) {
      toast.error(error.message || 'Google sign-up failed');
    }
  };

  const handleResendCode = async () => {
    if (!signupData || countdown > 0) return;
    
    setResending(true);
    try {
      // Resend verification code by triggering signup again
      await signUp(signupData.email, signupData.password, signupData.name);
      setCountdown(60);
      toast.success('New verification code sent!');
    } catch (error: any) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const goBackToSignup = () => {
    setStep('signup');
    setSignupData(null);
    clearError();
    otpForm.reset();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Sign Up Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 bg-white dark:bg-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md mx-auto"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TubeOps</h1>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Sign Up Form */}
            {step === 'signup' && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Create your account
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Start your journey to effortless content creation
                  </p>
                </div>

                {/* ADDED: Google OAuth Button */}
                {isGoogleEnabled && (
                  <div className="mb-6">
                    <GoogleOAuthButton
                      onSignIn={handleGoogleSignUp}
                      loading={isLoading}
                      text="Sign up with Google"
                    />
                    
                    <div className="mt-6 relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                          Or sign up with email
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={signupForm.handleSubmit(onSignUpSubmit)} className="space-y-6">
                  <Input
                    label="Full name"
                    type="text"
                    leftIcon={<User className="w-4 h-4" />}
                    placeholder="Enter your full name"
                    error={signupForm.formState.errors.name?.message}
                    {...signupForm.register('name')}
                  />

                  <Input
                    label="Email address"
                    type="email"
                    leftIcon={<Mail className="w-4 h-4" />}
                    placeholder="Enter your email"
                    error={signupForm.formState.errors.email?.message}
                    {...signupForm.register('email')}
                  />

                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    leftIcon={<Lock className="w-4 h-4" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                    placeholder="Create a strong password"
                    error={signupForm.formState.errors.password?.message}
                    {...signupForm.register('password')}
                  />

                  <Input
                    label="Confirm password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    leftIcon={<Lock className="w-4 h-4" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                    placeholder="Confirm your password"
                    error={signupForm.formState.errors.confirmPassword?.message}
                    {...signupForm.register('confirmPassword')}
                  />

                  <div className="flex items-start space-x-3">
                    <input
                      id="terms"
                      type="checkbox"
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-1"
                      {...signupForm.register('terms')}
                    />
                    <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
                      I agree to the{' '}
                      <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {signupForm.formState.errors.terms && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {signupForm.formState.errors.terms.message}
                    </p>
                  )}

                  {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
                    loading={isLoading}
                  >
                    Create Account
                  </Button>

                  <div className="text-center">
                    <span className="text-gray-600 dark:text-gray-400">Already have an account? </span>
                    <Link
                      to="/auth/signin"
                      className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
                    >
                      Sign in
                    </Link>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 2: OTP Verification - Same as before */}
            {step === 'verify' && (
              <motion.div
                key="verify"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Check your email
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    We've sent a 6-digit verification code to
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {signupData?.email}
                  </p>
                </div>

                <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-6">
                  <div>
                    <Input
                      label="Verification code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="text-center text-lg tracking-wider font-mono"
                      error={otpForm.formState.errors.code?.message}
                      {...otpForm.register('code', {
                        onChange: (e) => {
                          // Auto-submit when 6 digits entered
                          const value = e.target.value.replace(/\D/g, '');
                          e.target.value = value;
                          if (value.length === 6) {
                            otpForm.handleSubmit(onOTPSubmit)();
                          }
                        }
                      })}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enter the 6-digit code from your email
                    </p>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    loading={isLoading}
                    disabled={otpForm.watch('code')?.length !== 6}
                  >
                    Verify Email
                  </Button>

                  {/* Resend Code */}
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Didn't receive the code?
                    </p>
                    {countdown > 0 ? (
                      <p className="text-sm text-gray-500">
                        Request new code in {countdown}s
                      </p>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleResendCode}
                        loading={resending}
                        leftIcon={<RefreshCw className="w-4 h-4" />}
                      >
                        Resend code
                      </Button>
                    )}
                  </div>

                  {/* Back Button */}
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={goBackToSignup}
                      leftIcon={<ArrowLeft className="w-4 h-4" />}
                    >
                      Back to sign up
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 3: Success */}
            {step === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="w-20 h-20 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <motion.svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="w-10 h-10 text-purple-600 dark:text-purple-400"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </motion.svg>
                </motion.div>
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome to TubeOps! 🎉
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your account has been verified successfully. Let's get you set up!
                </p>
                
                <div className="animate-pulse">
                  <p className="text-sm text-purple-600 dark:text-purple-400">
                    Starting onboarding...
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Right Panel - Benefits (same as before) */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Zap className="w-7 h-7" />
              </div>
              <h1 className="text-3xl font-bold">TubeOps</h1>
            </div>
            <h2 className="text-4xl font-bold mb-4">
              {step === 'verify' ? 'Almost there!' : 'Start creating viral content today'}
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed mb-8">
              {step === 'verify' 
                ? 'Check your email and enter the verification code to complete your account setup.'
                : 'Join the creators revolution. Get started with our free plan and upgrade as you grow.'
              }
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Crown className="w-6 h-6 text-yellow-400" />
                <h3 className="font-semibold text-lg">Free Plan Includes:</h3>
              </div>
              <ul className="space-y-2 text-primary-100">
                <li>• 10 videos per month</li>
                <li>• Basic AI content generation</li>
                <li>• YouTube integration</li>
                <li>• Community support</li>
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Zap className="w-6 h-6 text-blue-400" />
                <h3 className="font-semibold text-lg">Pro Features:</h3>
              </div>
              <ul className="space-y-2 text-primary-100">
                <li>• Unlimited videos</li>
                <li>• Advanced scheduling</li>
                <li>• Analytics & insights</li>
                <li>• Priority support</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-white/10 rounded-full blur-xl" />
      </div>
    </div>
  );
};

export default SignUp;